import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import numeral from 'numeral';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlagCountry from 'src/components/common/FlagCountry';

// Component
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import Table from 'src/components/Table';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';

import { get, isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import { IListButtonItem } from 'src/components/common/ButtonGroupFilter';
import TreemapChart from 'src/components/HighChart/Treemap';
import { TREEMAP_PALLET_COLOR } from 'src/components/HighChart/Treemap/config';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import FilterChart from 'src/features/TradeData/Common/FilterChart';
import { TypeTabTradeData } from 'src/features/TradeData/config';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  useQueryChartCTDPortsAggregatedByBOL,
  useQueryListCTDPortsAggregatedByBOL,
  useQueryMapCTDPortsAggregatedByBOL,
} from 'src/services/queries/useQueryCompanyTradeData';
import { KeyFilterChartTradeDataUnitBy, TypePortData } from 'src/utils/constants/enum';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import { TypeQueryParamPortTransaction } from 'src/utils/typings/dto/tradeData';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

import dayjs from 'dayjs';
import { Point } from 'highcharts';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipTradeDataLimit from 'src/components/common/TooltipTradeDataLimit';
import WorldMapChart from 'src/components/HighChart/WorldMapChart';
import { WorldMapOptions } from 'src/components/HighChart/WorldMapChart/config';
import { LIST_RESTRICTION_DATA, useProvisionRestriction } from 'src/hooks/useProvisionRestriction';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { formatValueUSD } from 'src/utils/helpers/utilities';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';

const CompanyTradeDataViewByPorts = ({ selectedTab }: { selectedTab: TypeTabTradeData }) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const [unitFilter, setUnitFilter] = useState<IListButtonItem['key']>(KeyFilterChartTradeDataUnitBy.bol);
  const { id: companyId } = useParams();
  const { checkRestriction } = useProvisionRestriction();
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();
  const { setSort, pooSortParams, podSortParams } = useTradeDataSortStore();
  const theme = useTheme();

  const isPortOfOrigin = selectedTab === TypeTabTradeData.PortOrigin;

  const tableSortParams = isPortOfOrigin ? pooSortParams : podSortParams;

  const tableSortParamsName = isPortOfOrigin ? 'pooSortParams' : 'podSortParams';

  const [proportionTablePaging, setProportionTablePaging] = useState({
    page: 0,
    page_size: 10,
  });
  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime:
      queryParams.start_date ??
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_FROM, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    toTime:
      queryParams.end_date ??
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_TO, 'YYYY-MM-DD').format(FORMAT_DATE_TIME.yyyy_mm_dd),
  } as {
    fromTime: string;
    toTime: string;
  });

  const proportionChartRequestPayload: TypeQueryParamPortTransaction = useMemo(() => {
    return {
      ...queryParams,
      limit: 10,
      company_id: companyId || '',
      offset: 0,
      is_exp_port: isPortOfOrigin,
      sort_by: unitFilter,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      order: 'desc',
    };
  }, [queryParams, isPortOfOrigin, unitFilter, selectedRangeTime]);

  const proportionTableRequestPayload: TypeQueryParamPortTransaction = useMemo(() => {
    return {
      ...queryParams,
      company_id: companyId || '',
      limit: proportionTablePaging.page_size,
      offset: proportionTablePaging.page,
      is_exp_port: isPortOfOrigin,
      sort_by: tableSortParams?.sortBy,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      order: tableSortParams?.sort,
    };
  }, [queryParams, isPortOfOrigin, tableSortParams, selectedRangeTime, proportionTablePaging]);

  const mapRequestPayload: TypeQueryParamPortTransaction = useMemo(() => {
    return {
      ...queryParams,
      company_id: companyId || '',
      is_exp_port: isPortOfOrigin,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
    };
  }, [queryParams, isPortOfOrigin, tableSortParams, selectedRangeTime]);

  const {
    data: proportionalTableData,
    isFetching: proportionalTableIsFetching,
    dataMapping: proportionalTableDataMapping,
  } = useQueryListCTDPortsAggregatedByBOL(
    companyId || '',
    isPortOfOrigin ? TypePortData.poo : TypePortData.pod,
    proportionTableRequestPayload,
  );

  const { isFetching: isFetchingMap, dataMapping: mapDataMapping } = useQueryMapCTDPortsAggregatedByBOL(
    companyId || '',
    isPortOfOrigin ? TypePortData.poo : TypePortData.pod,
    mapRequestPayload,
  );

  const proportionTableConfigColumn: ConfigCell<ITradeDataByProportionalPort>[] = [
    {
      disablePadding: false,
      id: 'port_name',
      disableSort: true,
      label: isPortOfOrigin
        ? t('trade_data.port_origin.table.port_name')
        : t('trade_data.port_delivery.table.port_name'),
      cell: (data: ITradeDataByProportionalPort) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={data?.country_code || ''} />
            <Typography variant="body2" color="text.secondary">
              {renderTextOrDefault(data.port_name)}
            </Typography>
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(numeral(data.shipments).format('O,O'))}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(numeral(data.weight).format('O,O'))}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      width: 150,
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(numeral(data.teu).format('O,O'))}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      width: 200,
      cell: (data: ITradeDataByProportionalPort) => {
        const checkRestrictionValue = checkRestriction({
          countryCode: data?.country_code || '',
          typeData: LIST_RESTRICTION_DATA.VALUES_USD,
        });
        return (
          <Typography variant="body2" color="text.secondary">
            {formatValueUSD(data.value_usd)}
            {checkRestrictionValue && <TooltipTradeDataLimit />}
          </Typography>
        );
      },
    },
  ];

  const { isFetching: isFetchingChart, dataMapping: chartData } = useQueryChartCTDPortsAggregatedByBOL(
    companyId || '',
    isPortOfOrigin ? TypePortData.poo : TypePortData.pod,
    proportionChartRequestPayload,
  );

  const worldMapData = useMemo(() => {
    return mapDataMapping.map((item) => {
      return {
        ...item,
        lon: item?.longitude || item.port_country_longitude,
        lat: item?.latitude || item.port_country_latitude,
        name: item?.port_name || '',
        id: item?.port_locode,
      };
    });
  }, [mapDataMapping]);

  const renderChart = () => {
    if (isEmpty(chartData) && !isFetchingChart) {
      return (
        <CompanyInfoNoData
          title={t('trade_data.common.notice_no_data.title')}
          content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
          sxContainer={{
            borderWidth: 0,
          }}
        />
      );
    }
    return (
      <TreemapChart
        colors={TREEMAP_PALLET_COLOR}
        height={600}
        loading={isFetchingChart}
        tooltipFormatter={function () {
          const point: Highcharts.Point = this.point;
          const transactionDetail = chartData?.find(({ port_locode }) => port_locode === point?.name);

          const countryCode = transactionDetail?.country_code;
          const countryName = transactionDetail?.port_name;
          const value = get(transactionDetail, `${unitFilter}`, 0);
          const valuePercent = get(transactionDetail, `percentage_${unitFilter}`, 0);

          const flag = getEmojiFlag(countryCode || '');
          return `
            <div style="min-width: 270px">
              <span style="font-size: 24px; color: inherit; display: block; color: inherit; fontWeight: 600; margin-bottom: 5px">
                ${valuePercent}%
              </span>
              <span style="color: inherit; display: block; color: inherit; fontWeight: 600">
              ${flag} ${countryName || t('global.others')}
              </span>
              <span style="color: inherit; display: block; color: ${theme.palette.text.secondary}">${t('trade_data.filter_chart.unit.' + unitFilter)} <b style="color: #fff">
              ${numeral(value).format('O,O')}</b>
              </span>
            </div>`;
        }}
        dataLabelFormatter={function () {
          const point: Highcharts.Point = this.point;
          const transactionDetail = chartData?.find(({ port_locode }) => port_locode === point?.name);
          const countryCode = transactionDetail?.country_code;
          const countryName = transactionDetail?.port_name;
          const flag = getEmojiFlag(countryCode || '');
          return `<div style="font-size: 14px; font-weight: 500; color: ${theme.palette.secondary.main}">
          <p style="display: block">${flag}</p>
          <p style="display: block">${countryName}</p>
            </div>`;
        }}
        data={getArrayDefault(chartData).map((transaction, index) => ({
          name: transaction.port_locode,
          value: Math.cbrt(Number(get(transaction, unitFilter, ''))),
          sortIndex: index,
        }))}
      />
    );
  };

  const isDataReturnedEmpty =
    !proportionalTableIsFetching &&
    !isFetchingMap &&
    !isFetchingChart &&
    isEmpty(getArrayDefault(proportionalTableDataMapping)) &&
    isEmpty(getArrayDefault(mapDataMapping)) &&
    isEmpty(getArrayDefault(chartData));

  return (
    <Stack direction="column" spacing={3}>
      <WhiteBoxContent>
        <Stack direction="column" spacing={3}>
          <FilterChart
            onSelectTimeRange={({ fromTime, toTime }) => {
              setSelectedRangeTime({
                fromTime: fromTime,
                toTime: toTime,
              });
            }}
            title={
              <Stack direction={'row'} gap={1} alignItems={'center'}>
                <Typography variant="h5">
                  {isPortOfOrigin ? t('trade_data.port_origin.title_chart') : t('trade_data.port_delivery.title_chart')}
                </Typography>
                <Chip
                  variant="outlined"
                  color="info"
                  label={
                    <Typography fontSize={8} fontWeight={800} color={'inherit'}>
                      BETA
                    </Typography>
                  }
                  size="small"
                />
              </Stack>
            }
            onSelectUnitBy={(unit: IListButtonItem['key']) => {
              setSort(tableSortParamsName, {
                sortBy: unit,
                sort: 'desc',
              });
              setUnitFilter(unit);
            }}
            selectedUnitBy={unitFilter}
            selectedRangeTime={{
              fromTime: proportionChartRequestPayload.start_date || '',
              toTime: proportionChartRequestPayload.end_date || '',
            }}
          />

          {!isDataReturnedEmpty ? (
            renderChart()
          ) : (
            <CompanyInfoNoData
              title={t('trade_data.common.notice_no_data.title')}
              content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
            />
          )}
        </Stack>
      </WhiteBoxContent>

      {!isDataReturnedEmpty && (
        <WhiteBoxContent>
          <Stack direction="column" spacing={3}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <Typography gap={1} display={'flex'} variant="h5">
                {isPortOfOrigin ? 'Ports of Origin Map' : 'Ports of Delivery Map'}
              </Typography>
            </Stack>
            <WorldMapChart
              data={[]}
              dataPoint={worldMapData}
              tooltipFormatter={function () {
                const point: Point = this.point;
                const pointData = mapDataMapping.find((item) => item.port_locode === point.options.id);

                const flag = getEmojiFlag(pointData?.country_code || '');

                const portTypeLabel = isPortOfOrigin ? 'POO' : 'POD';

                return `<div><b style="color: ${theme.palette.text.secondary}">${portTypeLabel} </b> <b>${flag} ${pointData?.port_name}</b>
              <br/><b style="color: ${theme.palette.text.secondary}">Bill of Lading </b> <b>${renderNumberOrDefault(pointData?.bol)}</b>
              <br/><b style="color: ${theme.palette.text.secondary}">Weight(KG) </b> <b>${renderNumberOrDefault(pointData?.weight)}</b>
              <br/><b style="color: ${theme.palette.text.secondary}">Quantity </b> <b>${renderNumberOrDefault(pointData?.teu)}</b>
              <br/><b style="color: ${theme.palette.text.secondary}">Value(USD) </b> <b>--</b>
              </div>`;
              }}
              height={580}
              type={WorldMapOptions.ctd_ports}
            />
          </Stack>
        </WhiteBoxContent>
      )}
      {!isDataReturnedEmpty && (
        <WhiteBoxContent>
          <Stack direction="column" spacing={3}>
            <Typography textAlign="left" variant="h5">
              {isPortOfOrigin ? t('trade_data.port_origin.title_table') : t('trade_data.port_delivery.title_table')}
            </Typography>
            <Table
              handleSort={(data) => {
                setSort(tableSortParamsName, data);
              }}
              maxRow={10}
              onChangePage={(value) => {
                setProportionTablePaging({ ...proportionTablePaging, page: value });
              }}
              onChangePageSize={(value) => setProportionTablePaging({ page: 0, page_size: value })}
              loading={proportionalTableIsFetching}
              page={proportionalTableData.page}
              pageSize={proportionalTableData.page_size}
              totalRecords={proportionalTableData.total_records}
              dataKey={'id'}
              data={proportionalTableDataMapping}
              configColumn={proportionTableConfigColumn}
            />
          </Stack>
        </WhiteBoxContent>
      )}
    </Stack>
  );
};

export default CompanyTradeDataViewByPorts;
